<template>
  <div class="jwt-decoder-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <h1 class="hero-title">JWT Decoder</h1>
            <p class="hero-subtitle">
              Decode and inspect JSON Web Tokens (JWT). View header, payload, and signature. Perfect for debugging authentication and authorization.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Tool Section -->
    <section class="tool-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <!-- Input Section -->
            <div class="input-card mb-4">
              <div class="card-header">
                <h3>JWT Token</h3>
                <div class="card-actions">
                  <button class="btn-action" title="Clear" @click="clearInput">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                  <button class="btn-action" title="Paste" @click="pasteFromClipboard">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                    </svg>
                  </button>
                  <button class="btn-action" title="Load sample" @click="loadSample">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </button>
                </div>
              </div>
              <textarea
                v-model="jwtToken"
                class="jwt-textarea"
                :class="{ error: error, valid: isValid }"
                placeholder="Paste your JWT token here... e.g. eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c"
                @input="decodeToken"
              />
              <div v-if="error" class="status-message error">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {{ error }}
              </div>
              <div v-else-if="isValid" class="status-message success">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Valid JWT Token
              </div>
            </div>

            <!-- Decoded Sections -->
            <div v-if="decoded" class="decoded-container">
              <!-- Token Info -->
              <div v-if="tokenInfo" class="info-banner" :class="tokenInfo.status">
                <div class="info-content">
                  <div class="info-icon">
                    <svg v-if="tokenInfo.status === 'expired'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <svg v-else-if="tokenInfo.status === 'valid'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </div>
                  <div class="info-text">
                    <strong>{{ tokenInfo.title }}</strong>
                    <p>{{ tokenInfo.message }}</p>
                  </div>
                </div>
              </div>

              <!-- View Mode Toggle -->
              <div class="view-toggle mb-4">
                <button
                  class="toggle-btn"
                  :class="{ active: viewMode === 'table' }"
                  @click="viewMode = 'table'"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                  Table View
                </button>
                <button
                  class="toggle-btn"
                  :class="{ active: viewMode === 'json' }"
                  @click="viewMode = 'json'"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="16 18 22 12 16 6" />
                    <polyline points="8 6 2 12 8 18" />
                  </svg>
                  JSON View
                </button>
              </div>

              <div class="row">
                <!-- Header -->
                <div class="col-lg-12 mb-4">
                  <div class="decode-card header-card">
                    <div class="card-header">
                      <h3>
                        <span class="section-badge header-badge">Header</span>
                        Algorithm & Token Type
                      </h3>
                      <button
                        class="btn-action"
                        :class="{ success: headerCopied }"
                        title="Copy header JSON"
                        @click="copySection('header')"
                      >
                        <svg v-if="!headerCopied" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </button>
                    </div>
                    <!-- Table View -->
                    <div v-if="viewMode === 'table'" class="jwt-table">
                      <div v-for="[key, value] in Object.entries(decoded.header)" :key="key" class="table-row">
                        <div class="table-key">{{ key }}</div>
                        <div class="table-value">{{ formatValue(value) }}</div>
                      </div>
                    </div>
                    <!-- JSON View -->
                    <pre v-else class="json-output"><code>{{ decoded.headerFormatted }}</code></pre>
                  </div>
                </div>

                <!-- Payload -->
                <div class="col-lg-12 mb-4">
                  <div class="decode-card payload-card">
                    <div class="card-header">
                      <h3>
                        <span class="section-badge payload-badge">Payload</span>
                        Claims & Data
                      </h3>
                      <button
                        class="btn-action"
                        :class="{ success: payloadCopied }"
                        title="Copy payload JSON"
                        @click="copySection('payload')"
                      >
                        <svg v-if="!payloadCopied" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </button>
                    </div>
                    <!-- Table View -->
                    <div v-if="viewMode === 'table'" class="jwt-table">
                      <div v-for="[key, value] in Object.entries(decoded.payload)" :key="key" class="table-row">
                        <div class="table-key">
                          <span class="key-name">{{ key }}</span>
                          <span v-if="getClaimDescription(key)" class="key-description">{{ getClaimDescription(key) }}</span>
                        </div>
                        <div class="table-value">{{ formatPayloadValue(key, value) }}</div>
                      </div>
                    </div>
                    <!-- JSON View -->
                    <pre v-else class="json-output"><code>{{ decoded.payloadFormatted }}</code></pre>
                  </div>
                </div>
              </div>

              <!-- Signature -->
              <div class="decode-card signature-card">
                <div class="card-header">
                  <h3>
                    <span class="section-badge signature-badge">Signature</span>
                    Verify Token Integrity
                  </h3>
                  <button
                    class="btn-action"
                    :class="{ success: signatureCopied }"
                    title="Copy signature"
                    @click="copySection('signature')"
                  >
                    <svg v-if="!signatureCopied" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                </div>
                <code class="signature-value">{{ decoded.signature }}</code>
                <p class="signature-note">
                  ⚠️ This tool only decodes the JWT. To verify the signature, you need the secret key on the server side.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Info Section -->
    <section class="info-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="info-card">
              <h3>About JSON Web Tokens (JWT)</h3>
              <p>
                JWT is an open standard (RFC 7519) for securely transmitting information between parties as a JSON object. 
                JWTs are commonly used for authentication and information exchange in web applications.
              </p>
              
              <h4>JWT Structure</h4>
              <p>A JWT consists of three parts separated by dots (.):</p>
              <ul>
                <li><strong>Header</strong> - Contains the token type (JWT) and signing algorithm (e.g., HMAC SHA256, RSA)</li>
                <li><strong>Payload</strong> - Contains the claims (statements about the user and additional data)</li>
                <li><strong>Signature</strong> - Used to verify the token hasn't been tampered with</li>
              </ul>
              <p>Format: <code>header.payload.signature</code></p>
              
              <h4>Common Claims</h4>
              <ul>
                <li><strong>iss</strong> (issuer) - Who issued the token</li>
                <li><strong>sub</strong> (subject) - Who the token is about (typically user ID)</li>
                <li><strong>aud</strong> (audience) - Who the token is intended for</li>
                <li><strong>exp</strong> (expiration) - When the token expires (Unix timestamp)</li>
                <li><strong>iat</strong> (issued at) - When the token was issued (Unix timestamp)</li>
                <li><strong>nbf</strong> (not before) - Token not valid before this time</li>
              </ul>
              
              <h4>Use Cases</h4>
              <ul>
                <li><strong>Authentication</strong> - After login, each request includes the JWT</li>
                <li><strong>Information Exchange</strong> - Securely transmit information between parties</li>
                <li><strong>Single Sign-On (SSO)</strong> - Share authentication across multiple domains</li>
                <li><strong>API Authorization</strong> - Grant access to protected resources</li>
              </ul>
              
              <h4>Security Notes</h4>
              <p>
                <strong>⚠️ Important:</strong> JWTs are signed, not encrypted. Anyone can decode and read the contents. 
                Never store sensitive information like passwords in JWT payloads. Always use HTTPS when transmitting JWTs. 
                Validate tokens on the server side before trusting their contents.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

useHead({
  title: 'JWT Decoder | Developer Tools',
  meta: [
    { name: 'description', content: 'Decode and inspect JSON Web Tokens (JWT). View header, payload, and signature. Perfect for debugging authentication.' }
  ]
})

interface JWTHeader {
  alg: string
  typ: string
  kid?: string
  [key: string]: unknown
}

interface JWTPayload {
  iss?: string
  sub?: string
  aud?: string | string[]
  exp?: number
  nbf?: number
  iat?: number
  jti?: string
  [key: string]: unknown
}

interface DecodedJWT {
  header: JWTHeader
  payload: JWTPayload
  signature: string
  headerFormatted: string
  payloadFormatted: string
}

interface TokenInfo {
  status: 'valid' | 'expired' | 'not-yet-valid'
  title: string
  message: string
}

const jwtToken = ref('')
const decoded = ref<DecodedJWT | null>(null)
const error = ref('')
const isValid = ref(false)
const headerCopied = ref(false)
const payloadCopied = ref(false)
const signatureCopied = ref(false)
const viewMode = ref<'table' | 'json'>('table')

const tokenInfo = computed<TokenInfo | null>(() => {
  if (!decoded.value?.payload) return null

  const payload = decoded.value.payload
  const now = Math.floor(Date.now() / 1000)

  if (payload.exp && payload.exp < now) {
    const expiredDate = new Date(payload.exp * 1000).toLocaleString()
    return {
      status: 'expired',
      title: 'Token Expired',
      message: `This token expired on ${expiredDate}`
    }
  }

  if (payload.nbf && payload.nbf > now) {
    const validDate = new Date(payload.nbf * 1000).toLocaleString()
    return {
      status: 'not-yet-valid',
      title: 'Token Not Yet Valid',
      message: `This token will be valid starting ${validDate}`
    }
  }

  if (payload.exp) {
    const expiresDate = new Date(payload.exp * 1000).toLocaleString()
    return {
      status: 'valid',
      title: 'Token Valid',
      message: `This token expires on ${expiresDate}`
    }
  }

  return {
    status: 'valid',
    title: 'Token Decoded',
    message: 'No expiration time set on this token'
  }
})

const decodeToken = () => {
  error.value = ''
  decoded.value = null
  isValid.value = false

  if (!jwtToken.value.trim()) return

  try {
    const parts = jwtToken.value.trim().split('.')
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format. Must have 3 parts separated by dots.')
    }

    const [headerB64, payloadB64, signature] = parts

    // Decode header
    const headerJson = atob(headerB64.replace(/-/g, '+').replace(/_/g, '/'))
    const header = JSON.parse(headerJson)

    // Decode payload
    const payloadJson = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'))
    const payload = JSON.parse(payloadJson)

    // Format timestamps in payload
    const formattedPayload = { ...payload }
    if (payload.exp) {
      formattedPayload.exp = `${payload.exp} (${new Date(payload.exp * 1000).toLocaleString()})`
    }
    if (payload.iat) {
      formattedPayload.iat = `${payload.iat} (${new Date(payload.iat * 1000).toLocaleString()})`
    }
    if (payload.nbf) {
      formattedPayload.nbf = `${payload.nbf} (${new Date(payload.nbf * 1000).toLocaleString()})`
    }

    decoded.value = {
      header,
      payload,
      signature,
      headerFormatted: JSON.stringify(header, null, 2),
      payloadFormatted: JSON.stringify(formattedPayload, null, 2)
    }

    isValid.value = true
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : 'Invalid JWT token'
    decoded.value = null
  }
}

const clearInput = () => {
  jwtToken.value = ''
  decoded.value = null
  error.value = ''
  isValid.value = false
}

const pasteFromClipboard = async () => {
  try {
    const text = await navigator.clipboard.readText()
    jwtToken.value = text
    decodeToken()
  } catch (err) {
    console.error('Failed to paste:', err)
  }
}

const loadSample = () => {
  jwtToken.value = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiZW1haWwiOiJqb2huQGV4YW1wbGUuY29tIiwicm9sZSI6ImFkbWluIiwiaWF0IjoxNTE2MjM5MDIyLCJleHAiOjE5MTYyMzkwMjJ9.4Adcj0prx8P4SEkuOHQXt8aTqzDXsNCXMXcXEcKxHuE'
  decodeToken()
}

const formatValue = (value: unknown): string => {
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value)
  }
  return String(value)
}

const formatPayloadValue = (key: string, value: unknown): string => {
  // Format timestamp fields
  if (['exp', 'iat', 'nbf'].includes(key) && typeof value === 'number') {
    return `${value} (${new Date(value * 1000).toLocaleString()})`
  }
  return formatValue(value)
}

const getClaimDescription = (key: string): string => {
  const descriptions: Record<string, string> = {
    'iss': 'Issuer',
    'sub': 'Subject',
    'aud': 'Audience',
    'exp': 'Expiration Time',
    'iat': 'Issued At',
    'nbf': 'Not Before',
    'jti': 'JWT ID',
    'alg': 'Algorithm',
    'typ': 'Type'
  }
  return descriptions[key] || ''
}

const copySection = async (section: 'header' | 'payload' | 'signature') => {
  if (!decoded.value) return

  let text = ''
  let copiedRef = headerCopied

  if (section === 'header') {
    text = decoded.value.headerFormatted
    copiedRef = headerCopied
  } else if (section === 'payload') {
    text = decoded.value.payloadFormatted
    copiedRef = payloadCopied
  } else {
    text = decoded.value.signature
    copiedRef = signatureCopied
  }

  try {
    await navigator.clipboard.writeText(text)
    copiedRef.value = true
    setTimeout(() => {
      copiedRef.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}
</script>

<style scoped>
.jwt-decoder-page {
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
}

.hero-section {
  padding: 4rem 0 2rem;
  text-align: center;
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #4a5568;
  max-width: 700px;
  margin: 0 auto;
}

.tool-section {
  padding: 2rem 0 4rem;
}

.input-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.card-header h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-action {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #4a5568;
}

.btn-action:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f7fafc;
}

.btn-action.success {
  border-color: #48bb78;
  color: #48bb78;
  background: #f0fff4;
}

.jwt-textarea {
  width: 100%;
  min-height: 120px;
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  resize: vertical;
  transition: all 0.2s ease;
  line-height: 1.6;
}

.jwt-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.jwt-textarea.error {
  border-color: #f56565;
}

.jwt-textarea.valid {
  border-color: #48bb78;
}

.status-message {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 6px;
}

.status-message.error {
  color: #f56565;
  background: #fff5f5;
}

.status-message.success {
  color: #48bb78;
  background: #f0fff4;
}

.decoded-container {
  margin-top: 2rem;
}

.view-toggle {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  background: white;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.toggle-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  color: #4a5568;
  transition: all 0.2s ease;
}

.toggle-btn:hover {
  border-color: #667eea;
  background: #f7fafc;
}

.toggle-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  color: white;
}

.toggle-btn svg {
  width: 16px;
  height: 16px;
}

.info-banner {
  padding: 1.25rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  border-left: 4px solid;
}

.info-banner.valid {
  background: #f0fff4;
  border-color: #48bb78;
}

.info-banner.expired {
  background: #fff5f5;
  border-color: #f56565;
}

.info-banner.not-yet-valid {
  background: #fffaf0;
  border-color: #ed8936;
}

.info-content {
  display: flex;
  gap: 1rem;
}

.info-icon {
  flex-shrink: 0;
}

.info-banner.valid .info-icon {
  color: #48bb78;
}

.info-banner.expired .info-icon {
  color: #f56565;
}

.info-banner.not-yet-valid .info-icon {
  color: #ed8936;
}

.info-text strong {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 1rem;
}

.info-text p {
  margin: 0;
  color: #4a5568;
  font-size: 0.9rem;
}

.decode-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;
}

.section-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

.header-badge {
  background: #e0e7ff;
  color: #5a67d8;
}

.payload-badge {
  background: #fef3c7;
  color: #d97706;
}

.signature-badge {
  background: #dbeafe;
  color: #2563eb;
}

.jwt-table {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.table-row {
  display: grid;
  grid-template-columns: minmax(120px, 200px) 1fr;
  border-bottom: 1px solid #e2e8f0;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row:hover {
  background: #f7fafc;
}

.table-key {
  padding: 0.875rem 1rem;
  background: #f7fafc;
  font-weight: 600;
  color: #2d3748;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.key-name {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
}

.key-description {
  font-size: 0.75rem;
  font-weight: 400;
  color: #718096;
}

.table-value {
  padding: 0.875rem 1rem;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  color: #4a5568;
  word-break: break-word;
  background: white;
}

.json-output {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  overflow-x: auto;
  margin: 0;
  max-height: 400px;
}

.json-output code {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  color: #2d3748;
  white-space: pre;
}

.signature-card {
  margin-top: 1rem;
}

.signature-value {
  display: block;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  color: #2d3748;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  word-break: break-all;
  line-height: 1.6;
}

.signature-note {
  margin-top: 0.75rem;
  font-size: 0.875rem;
  color: #ed8936;
  background: #fffaf0;
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 0;
}

.info-section {
  padding: 4rem 0;
  background: white;
}

.info-card {
  padding: 2rem;
}

.info-card h3 {
  color: #1a202c;
  font-weight: 700;
  margin-bottom: 1rem;
}

.info-card h4 {
  color: #2d3748;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.info-card p {
  color: #4a5568;
  line-height: 1.7;
}

.info-card ul {
  color: #4a5568;
  line-height: 1.8;
}

.info-card code {
  background: #f7fafc;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  color: #667eea;
  font-size: 0.9em;
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .view-toggle {
    flex-direction: column;
  }
  
  .toggle-btn {
    width: 100%;
    justify-content: center;
  }
  
  .table-row {
    grid-template-columns: 1fr;
  }
  
  .table-key {
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
  }
}
</style>
